import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table2,
  Webhook,
  Zap,
  Calendar,
  Link2,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Trash2,
  Edit3,
  Pause,
  Play,
  Activity,
  ExternalLink,
  Send,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

type IntegrationStatus = 'connected' | 'not_connected' | 'coming_soon';
type ActiveIntegrationStatus = 'active' | 'error' | 'paused';
type SyncFrequency = 'realtime' | 'every_15_min' | 'hourly' | 'daily';

interface IntegrationCatalogItem {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  status: IntegrationStatus;
  color: string;
}

interface GoogleSheetsConfig {
  spreadsheetUrl: string;
  syncBookings: boolean;
  syncOrders: boolean;
  syncCustomers: boolean;
  syncPayments: boolean;
  syncFrequency: SyncFrequency;
  enabled: boolean;
}

interface WebhookConfig {
  webhookUrl: string;
  secretKey: string;
  eventBookingCreated: boolean;
  eventBookingUpdated: boolean;
  eventOrderCreated: boolean;
  eventPaymentReceived: boolean;
  eventMessageReceived: boolean;
  active: boolean;
}

interface ActiveIntegration {
  id: string;
  type: 'google_sheets' | 'webhook';
  name: string;
  status: ActiveIntegrationStatus;
  lastSync: string;
  eventsSent: number;
  config: GoogleSheetsConfig | WebhookConfig;
}

interface ActivityLogEntry {
  id: string;
  timestamp: string;
  integrationName: string;
  integrationType: 'google_sheets' | 'webhook';
  event: string;
  status: 'success' | 'error';
  details: string;
}

// ============================================================================
// Stubbed API (swap with real endpoints later)
// ============================================================================

const integrationsApi = {
  async listActive(_businessId: string): Promise<ActiveIntegration[]> {
    return [];
  },

  async saveGoogleSheets(_businessId: string, _config: GoogleSheetsConfig): Promise<ActiveIntegration> {
    return {
      id: `gs-${Date.now()}`,
      type: 'google_sheets',
      name: 'Google Sheets',
      status: 'active',
      lastSync: new Date().toISOString(),
      eventsSent: 0,
      config: _config,
    };
  },

  async saveWebhook(_businessId: string, _config: WebhookConfig): Promise<ActiveIntegration> {
    return {
      id: `wh-${Date.now()}`,
      type: 'webhook',
      name: 'Webhook Outbound',
      status: 'active',
      lastSync: new Date().toISOString(),
      eventsSent: 0,
      config: _config,
    };
  },

  async deleteIntegration(_businessId: string, _integrationId: string): Promise<void> {
    return;
  },

  async toggleIntegration(_businessId: string, _integrationId: string, _enabled: boolean): Promise<void> {
    return;
  },

  async testGoogleSheets(_businessId: string, _config: GoogleSheetsConfig): Promise<{ success: boolean; message: string }> {
    return { success: true, message: 'Connection successful' };
  },

  async testWebhook(_businessId: string, _config: WebhookConfig): Promise<{ success: boolean; payload: object }> {
    return {
      success: true,
      payload: {
        event: 'booking.created',
        timestamp: new Date().toISOString(),
        data: {
          id: 'bk_sample_123',
          customer: 'Maria Silva',
          service: 'Haircut',
          date: '2026-02-10',
          time: '14:00',
          amount: 45.00,
        },
      },
    };
  },

  async getActivityLog(_businessId: string): Promise<ActivityLogEntry[]> {
    return [];
  },
};

// ============================================================================
// Mock data generators
// ============================================================================

function generateMockActiveIntegrations(): ActiveIntegration[] {
  return [
    {
      id: 'gs-001',
      type: 'google_sheets',
      name: 'Google Sheets',
      status: 'active',
      lastSync: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      eventsSent: 1247,
      config: {
        spreadsheetUrl: 'https://docs.google.com/spreadsheets/d/1aBc...',
        syncBookings: true,
        syncOrders: true,
        syncCustomers: true,
        syncPayments: false,
        syncFrequency: 'every_15_min',
        enabled: true,
      } as GoogleSheetsConfig,
    },
    {
      id: 'wh-001',
      type: 'webhook',
      name: 'Webhook Outbound',
      status: 'active',
      lastSync: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      eventsSent: 3891,
      config: {
        webhookUrl: 'https://api.example.com/webhooks/sidehustle',
        secretKey: 'whsec_abc123...',
        eventBookingCreated: true,
        eventBookingUpdated: true,
        eventOrderCreated: true,
        eventPaymentReceived: true,
        eventMessageReceived: false,
        active: true,
      } as WebhookConfig,
    },
  ];
}

function generateMockActivityLog(): ActivityLogEntry[] {
  const now = Date.now();
  return [
    {
      id: 'log-1',
      timestamp: new Date(now - 2 * 60 * 1000).toISOString(),
      integrationName: 'Webhook Outbound',
      integrationType: 'webhook',
      event: 'booking.created',
      status: 'success',
      details: 'Delivered to https://api.example.com/webhooks (200 OK, 142ms)',
    },
    {
      id: 'log-2',
      timestamp: new Date(now - 5 * 60 * 1000).toISOString(),
      integrationName: 'Google Sheets',
      integrationType: 'google_sheets',
      event: 'sync.completed',
      status: 'success',
      details: 'Synced 3 new bookings, 1 updated order',
    },
    {
      id: 'log-3',
      timestamp: new Date(now - 15 * 60 * 1000).toISOString(),
      integrationName: 'Webhook Outbound',
      integrationType: 'webhook',
      event: 'payment.received',
      status: 'success',
      details: 'Delivered to https://api.example.com/webhooks (200 OK, 98ms)',
    },
    {
      id: 'log-4',
      timestamp: new Date(now - 32 * 60 * 1000).toISOString(),
      integrationName: 'Webhook Outbound',
      integrationType: 'webhook',
      event: 'order.created',
      status: 'error',
      details: 'Failed: Connection timeout after 30s. Will retry in 60s.',
    },
    {
      id: 'log-5',
      timestamp: new Date(now - 60 * 60 * 1000).toISOString(),
      integrationName: 'Google Sheets',
      integrationType: 'google_sheets',
      event: 'sync.completed',
      status: 'success',
      details: 'Synced 7 new bookings, 2 new customers',
    },
  ];
}

// ============================================================================
// Helper functions
// ============================================================================

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// Google Sheets Config Dialog
// ============================================================================

function GoogleSheetsDialog({
  open,
  onOpenChange,
  initialConfig,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig: GoogleSheetsConfig | null;
  onSave: (config: GoogleSheetsConfig) => void;
}) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<GoogleSheetsConfig>(
    initialConfig || {
      spreadsheetUrl: '',
      syncBookings: true,
      syncOrders: false,
      syncCustomers: false,
      syncPayments: false,
      syncFrequency: 'every_15_min',
      enabled: true,
    },
  );
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const urlValid = config.spreadsheetUrl === '' || isValidUrl(config.spreadsheetUrl);
  const hasSyncTarget = config.syncBookings || config.syncOrders || config.syncCustomers || config.syncPayments;
  const canSave = config.spreadsheetUrl !== '' && urlValid && hasSyncTarget;

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await integrationsApi.testGoogleSheets('', config);
      setTestResult(result);
    } catch {
      setTestResult({ success: false, message: t('integrations.testFailed') });
    } finally {
      setTesting(false);
    }
  }, [config, t]);

  const handleSave = useCallback(() => {
    onSave(config);
    onOpenChange(false);
  }, [config, onSave, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Table2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
            {t('integrations.googleSheets.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('integrations.googleSheets.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          {/* Spreadsheet URL */}
          <div className="grid gap-2">
            <Label htmlFor="spreadsheet-url">{t('integrations.googleSheets.spreadsheetUrl')}</Label>
            <Input
              id="spreadsheet-url"
              value={config.spreadsheetUrl}
              onChange={(e) => setConfig({ ...config, spreadsheetUrl: e.target.value })}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className={`border-[hsl(160,60%,18%)]/20 focus:border-[hsl(160,84%,39%)] ${
                !urlValid ? 'border-red-500' : ''
              }`}
            />
            {!urlValid && (
              <p className="text-xs text-red-500">{t('integrations.invalidUrl')}</p>
            )}
          </div>

          {/* Data to sync */}
          <div className="grid gap-3">
            <Label>{t('integrations.googleSheets.dataToSync')}</Label>
            <div className="space-y-3">
              {([
                ['syncBookings', t('integrations.googleSheets.bookings')],
                ['syncOrders', t('integrations.googleSheets.orders')],
                ['syncCustomers', t('integrations.googleSheets.customers')],
                ['syncPayments', t('integrations.googleSheets.payments')],
              ] as const).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <span className="text-sm font-medium">{label}</span>
                  <Switch
                    checked={config[key]}
                    onCheckedChange={(checked) => setConfig({ ...config, [key]: checked })}
                  />
                </div>
              ))}
            </div>
            {!hasSyncTarget && (
              <p className="text-xs text-amber-600">{t('integrations.googleSheets.selectAtLeastOne')}</p>
            )}
          </div>

          {/* Sync frequency */}
          <div className="grid gap-2">
            <Label>{t('integrations.googleSheets.syncFrequency')}</Label>
            <Select
              value={config.syncFrequency}
              onValueChange={(val) => setConfig({ ...config, syncFrequency: val as SyncFrequency })}
            >
              <SelectTrigger className="border-[hsl(160,60%,18%)]/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">{t('integrations.frequency.realtime')}</SelectItem>
                <SelectItem value="every_15_min">{t('integrations.frequency.every15Min')}</SelectItem>
                <SelectItem value="hourly">{t('integrations.frequency.hourly')}</SelectItem>
                <SelectItem value="daily">{t('integrations.frequency.daily')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(160,60%,18%)]/10">
            <div>
              <p className="text-sm font-medium">{t('integrations.enableIntegration')}</p>
              <p className="text-xs text-muted-foreground">{t('integrations.enableIntegrationDesc')}</p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
            />
          </div>

          {/* Test result */}
          {testResult && (
            <div
              className={`rounded-md px-4 py-3 border ${
                testResult.success
                  ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                  : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}
            >
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <p className="text-sm">{testResult.message}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || !config.spreadsheetUrl || !urlValid}
            className="sm:mr-auto border-[hsl(160,60%,18%)]/20"
          >
            {testing ? t('integrations.testing') : t('integrations.testConnection')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[hsl(160,60%,18%)]/20"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Webhook Config Dialog
// ============================================================================

function WebhookDialog({
  open,
  onOpenChange,
  initialConfig,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig: WebhookConfig | null;
  onSave: (config: WebhookConfig) => void;
}) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<WebhookConfig>(
    initialConfig || {
      webhookUrl: '',
      secretKey: '',
      eventBookingCreated: true,
      eventBookingUpdated: true,
      eventOrderCreated: false,
      eventPaymentReceived: false,
      eventMessageReceived: false,
      active: true,
    },
  );
  const [testing, setTesting] = useState(false);
  const [testPayload, setTestPayload] = useState<object | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const urlValid = config.webhookUrl === '' || isValidUrl(config.webhookUrl);
  const hasEvent =
    config.eventBookingCreated ||
    config.eventBookingUpdated ||
    config.eventOrderCreated ||
    config.eventPaymentReceived ||
    config.eventMessageReceived;
  const canSave = config.webhookUrl !== '' && urlValid && hasEvent;

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestPayload(null);
    try {
      const result = await integrationsApi.testWebhook('', config);
      setTestPayload(result.payload);
    } catch {
      setTestPayload(null);
    } finally {
      setTesting(false);
    }
  }, [config]);

  const handleSave = useCallback(() => {
    onSave(config);
    onOpenChange(false);
  }, [config, onSave, onOpenChange]);

  const webhookEvents = [
    { key: 'eventBookingCreated' as const, label: 'booking.created' },
    { key: 'eventBookingUpdated' as const, label: 'booking.updated' },
    { key: 'eventOrderCreated' as const, label: 'order.created' },
    { key: 'eventPaymentReceived' as const, label: 'payment.received' },
    { key: 'eventMessageReceived' as const, label: 'message.received' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-[hsl(160,84%,39%)]" />
            {t('integrations.webhook.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('integrations.webhook.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          {/* Webhook URL */}
          <div className="grid gap-2">
            <Label htmlFor="webhook-url">{t('integrations.webhook.endpointUrl')}</Label>
            <Input
              id="webhook-url"
              value={config.webhookUrl}
              onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
              placeholder="https://api.example.com/webhooks"
              className={`border-[hsl(160,60%,18%)]/20 focus:border-[hsl(160,84%,39%)] ${
                !urlValid ? 'border-red-500' : ''
              }`}
            />
            {!urlValid && (
              <p className="text-xs text-red-500">{t('integrations.invalidUrl')}</p>
            )}
          </div>

          {/* Secret key */}
          <div className="grid gap-2">
            <Label htmlFor="secret-key">{t('integrations.webhook.secretKey')}</Label>
            <div className="relative">
              <Input
                id="secret-key"
                type={showSecret ? 'text' : 'password'}
                value={config.secretKey}
                onChange={(e) => setConfig({ ...config, secretKey: e.target.value })}
                placeholder="whsec_..."
                className="pr-10 border-[hsl(160,60%,18%)]/20 focus:border-[hsl(160,84%,39%)]"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{t('integrations.webhook.secretKeyHelp')}</p>
          </div>

          {/* Events to send */}
          <div className="grid gap-3">
            <Label>{t('integrations.webhook.eventsToSend')}</Label>
            <div className="space-y-2">
              {webhookEvents.map(({ key, label }) => (
                <div
                  key={key}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <span className="text-sm font-mono">{label}</span>
                  <Switch
                    checked={config[key]}
                    onCheckedChange={(checked) => setConfig({ ...config, [key]: checked })}
                  />
                </div>
              ))}
            </div>
            {!hasEvent && (
              <p className="text-xs text-amber-600">{t('integrations.webhook.selectAtLeastOneEvent')}</p>
            )}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(160,60%,18%)]/10">
            <div>
              <p className="text-sm font-medium">{t('integrations.webhook.activeToggle')}</p>
              <p className="text-xs text-muted-foreground">{t('integrations.webhook.activeToggleDesc')}</p>
            </div>
            <Switch
              checked={config.active}
              onCheckedChange={(checked) => setConfig({ ...config, active: checked })}
            />
          </div>

          {/* Test payload */}
          {testPayload && (
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t('integrations.webhook.samplePayload')}</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(testPayload, null, 2))}
                >
                  <Copy className="h-3 w-3" />
                  {t('integrations.webhook.copy')}
                </Button>
              </div>
              <pre className="rounded-lg bg-muted p-4 text-xs font-mono overflow-x-auto max-h-48">
                {JSON.stringify(testPayload, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || !config.webhookUrl || !urlValid}
            className="sm:mr-auto gap-1.5 border-[hsl(160,60%,18%)]/20"
          >
            <Send className="h-4 w-4" />
            {testing ? t('integrations.testing') : t('integrations.webhook.testWebhook')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[hsl(160,60%,18%)]/20"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Integrations Page
// ============================================================================

function IntegrationsPage() {
  const { t } = useTranslation();
  const { operator, isLoading, selectedBusiness, selectedBusinessId } = useOperator();

  // Dialog state
  const [sheetsDialogOpen, setSheetsDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<ActiveIntegration | null>(null);

  // Local data (mock -- to be replaced by API calls)
  const [activeIntegrations, setActiveIntegrations] = useState<ActiveIntegration[]>(
    generateMockActiveIntegrations,
  );
  const [activityLog] = useState<ActivityLogEntry[]>(generateMockActivityLog);

  // Integration catalog
  const catalog: IntegrationCatalogItem[] = [
    {
      id: 'google_sheets',
      name: t('integrations.catalog.googleSheets'),
      description: t('integrations.catalog.googleSheetsDesc'),
      icon: Table2,
      status: activeIntegrations.some((i) => i.type === 'google_sheets') ? 'connected' : 'not_connected',
      color: '#0F9D58',
    },
    {
      id: 'webhook',
      name: t('integrations.catalog.webhook'),
      description: t('integrations.catalog.webhookDesc'),
      icon: Webhook,
      status: activeIntegrations.some((i) => i.type === 'webhook') ? 'connected' : 'not_connected',
      color: '#6366F1',
    },
    {
      id: 'zapier',
      name: t('integrations.catalog.zapier'),
      description: t('integrations.catalog.zapierDesc'),
      icon: Zap,
      status: 'coming_soon',
      color: '#FF4A00',
    },
    {
      id: 'google_calendar',
      name: t('integrations.catalog.googleCalendar'),
      description: t('integrations.catalog.googleCalendarDesc'),
      icon: Calendar,
      status: 'coming_soon',
      color: '#4285F4',
    },
  ];

  // Handlers
  const handleCatalogConfigure = useCallback(
    (item: IntegrationCatalogItem) => {
      if (item.status === 'coming_soon') return;

      const existing = activeIntegrations.find(
        (i) =>
          (item.id === 'google_sheets' && i.type === 'google_sheets') ||
          (item.id === 'webhook' && i.type === 'webhook'),
      );

      if (item.id === 'google_sheets') {
        setEditingIntegration(existing || null);
        setSheetsDialogOpen(true);
      } else if (item.id === 'webhook') {
        setEditingIntegration(existing || null);
        setWebhookDialogOpen(true);
      }
    },
    [activeIntegrations],
  );

  const handleSaveGoogleSheets = useCallback(
    (config: GoogleSheetsConfig) => {
      setActiveIntegrations((prev) => {
        const existingIdx = prev.findIndex((i) => i.type === 'google_sheets');
        const entry: ActiveIntegration = {
          id: editingIntegration?.id || `gs-${Date.now()}`,
          type: 'google_sheets',
          name: 'Google Sheets',
          status: config.enabled ? 'active' : 'paused',
          lastSync: new Date().toISOString(),
          eventsSent: editingIntegration?.eventsSent || 0,
          config,
        };
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = entry;
          return updated;
        }
        return [...prev, entry];
      });
      setEditingIntegration(null);
    },
    [editingIntegration],
  );

  const handleSaveWebhook = useCallback(
    (config: WebhookConfig) => {
      setActiveIntegrations((prev) => {
        const existingIdx = prev.findIndex((i) => i.type === 'webhook');
        const entry: ActiveIntegration = {
          id: editingIntegration?.id || `wh-${Date.now()}`,
          type: 'webhook',
          name: 'Webhook Outbound',
          status: config.active ? 'active' : 'paused',
          lastSync: new Date().toISOString(),
          eventsSent: editingIntegration?.eventsSent || 0,
          config,
        };
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = entry;
          return updated;
        }
        return [...prev, entry];
      });
      setEditingIntegration(null);
    },
    [editingIntegration],
  );

  const handleDeleteIntegration = useCallback((id: string) => {
    setActiveIntegrations((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const handleToggleIntegration = useCallback((id: string) => {
    setActiveIntegrations((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        const newStatus: ActiveIntegrationStatus = i.status === 'active' ? 'paused' : 'active';
        return { ...i, status: newStatus };
      }),
    );
  }, []);

  const handleEditIntegration = useCallback(
    (integration: ActiveIntegration) => {
      setEditingIntegration(integration);
      if (integration.type === 'google_sheets') {
        setSheetsDialogOpen(true);
      } else if (integration.type === 'webhook') {
        setWebhookDialogOpen(true);
      }
    },
    [],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) return null;

  // No business selected
  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Link2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t('integrations.selectBusiness')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const connectedCount = activeIntegrations.length;

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('integrations.title')}</h1>
        <p className="text-muted-foreground">
          {selectedBusiness?.name && `${selectedBusiness.name} - `}
          {t('integrations.subtitle')}
        </p>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-[hsl(160,84%,39%)]/5 border border-[hsl(160,84%,39%)]/20">
        <div className="w-12 h-12 rounded-xl bg-[hsl(160,84%,39%)] flex items-center justify-center text-white">
          <Link2 className="h-6 w-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-foreground">
            {connectedCount}/{catalog.filter((c) => c.status !== 'coming_soon').length}
          </div>
          <div className="text-sm text-muted-foreground">{t('integrations.activeConnections')}</div>
        </div>
      </div>

      {/* Integration Catalog */}
      <section>
        <h2 className="text-lg font-semibold mb-4">{t('integrations.availableIntegrations')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {catalog.map((item) => {
            const IconComponent = item.icon;
            const isComingSoon = item.status === 'coming_soon';
            const isConnected = item.status === 'connected';

            return (
              <div
                key={item.id}
                className={`group relative p-5 rounded-xl border-2 transition-all duration-300 ${
                  isComingSoon
                    ? 'border-border bg-muted/20 opacity-75'
                    : isConnected
                      ? 'border-[hsl(160,84%,39%)]/30 bg-[hsl(160,84%,39%)]/[0.03] hover:shadow-lg hover:-translate-y-0.5'
                      : 'border-border hover:border-[hsl(160,60%,18%)]/20 hover:shadow-lg hover:-translate-y-0.5'
                }`}
              >
                {/* Status badge */}
                <div className="absolute top-4 right-4">
                  {isConnected ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(160,84%,39%)]/10 text-[hsl(160,60%,18%)]">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">{t('integrations.connected')}</span>
                    </div>
                  ) : isComingSoon ? (
                    <Badge variant="secondary" className="text-xs">
                      {t('integrations.comingSoon')}
                    </Badge>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
                      <Circle className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">{t('integrations.notConnected')}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: `${item.color}15`,
                      color: item.color,
                    }}
                  >
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0 pr-24">
                    <h3 className="font-semibold text-foreground">{item.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border/50">
                  {isComingSoon ? (
                    <p className="text-xs text-muted-foreground italic">
                      {t('integrations.comingSoonNote')}
                    </p>
                  ) : (
                    <Button
                      onClick={() => handleCatalogConfigure(item)}
                      className={`w-full h-9 shadow-sm transition-all duration-200 ${
                        isConnected
                          ? 'bg-transparent border border-[hsl(160,60%,18%)]/20 text-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,18%)]/5'
                          : 'text-white'
                      }`}
                      style={!isConnected ? { backgroundColor: item.color } : undefined}
                      variant={isConnected ? 'outline' : 'default'}
                    >
                      {isConnected ? t('integrations.configure') : t('integrations.connect')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Active Integrations List */}
      {activeIntegrations.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-4">{t('integrations.activeIntegrationsList')}</h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {activeIntegrations.map((integration) => {
                  const IconComponent = integration.type === 'google_sheets' ? Table2 : Webhook;
                  const iconColor = integration.type === 'google_sheets' ? '#0F9D58' : '#6366F1';

                  return (
                    <div
                      key={integration.id}
                      className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors"
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
                      >
                        <IconComponent className="h-5 w-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-foreground">{integration.name}</h4>
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 ${
                              integration.status === 'active'
                                ? 'border-green-500/30 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400'
                                : integration.status === 'error'
                                  ? 'border-red-500/30 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
                                  : 'border-amber-500/30 text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400'
                            }`}
                          >
                            {integration.status === 'active'
                              ? t('integrations.statusActive')
                              : integration.status === 'error'
                                ? t('integrations.statusError')
                                : t('integrations.statusPaused')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {t('integrations.lastSync')}: {formatRelativeTime(integration.lastSync)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            {t('integrations.eventsSent', { count: integration.eventsSent })}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEditIntegration(integration)}
                          title={t('common.edit')}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleToggleIntegration(integration.id)}
                          title={integration.status === 'active' ? t('integrations.pause') : t('integrations.resume')}
                        >
                          {integration.status === 'active' ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:text-red-600"
                          onClick={() => handleDeleteIntegration(integration.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Activity Log */}
      <section>
        <h2 className="text-lg font-semibold mb-4">{t('integrations.activityLog')}</h2>
        {activityLog.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Activity className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">{t('integrations.noActivity')}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {activityLog.map((entry) => {
                  const IconComponent = entry.integrationType === 'google_sheets' ? Table2 : Webhook;
                  const isError = entry.status === 'error';

                  return (
                    <div key={entry.id} className="flex items-start gap-3 p-4">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          isError
                            ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-[hsl(160,84%,39%)]/10 text-[hsl(160,60%,18%)]'
                        }`}
                      >
                        {isError ? <AlertCircle className="h-4 w-4" /> : <IconComponent className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{entry.integrationName}</span>
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 font-mono"
                          >
                            {entry.event}
                          </Badge>
                          {isError && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 border-red-500/30 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
                            >
                              {t('integrations.statusError')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{entry.details}</p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                        {formatRelativeTime(entry.timestamp)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Request integration CTA */}
      <div className="text-center p-6 rounded-xl bg-muted/30 border border-dashed border-border">
        <Link2 className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground mb-3">
          {t('integrations.needDifferent')}
        </p>
        <Button variant="outline" className="gap-2 border-[hsl(160,60%,18%)]/20 hover:bg-[hsl(160,60%,18%)]/5">
          <ExternalLink className="h-4 w-4" />
          {t('integrations.requestIntegration')}
        </Button>
      </div>

      {/* Dialogs */}
      <GoogleSheetsDialog
        open={sheetsDialogOpen}
        onOpenChange={(open) => {
          setSheetsDialogOpen(open);
          if (!open) setEditingIntegration(null);
        }}
        initialConfig={
          editingIntegration?.type === 'google_sheets'
            ? (editingIntegration.config as GoogleSheetsConfig)
            : null
        }
        onSave={handleSaveGoogleSheets}
      />
      <WebhookDialog
        open={webhookDialogOpen}
        onOpenChange={(open) => {
          setWebhookDialogOpen(open);
          if (!open) setEditingIntegration(null);
        }}
        initialConfig={
          editingIntegration?.type === 'webhook'
            ? (editingIntegration.config as WebhookConfig)
            : null
        }
        onSave={handleSaveWebhook}
      />
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/integrations')({
  component: IntegrationsPage,
});
